import { Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/Button'
import { CountryDropdown } from '@/components/ui/CountryDropdown'
import { Checkbox, DatePicker, Field, Select } from '@/components/ui/Field'
import { VendorPicker } from '@/features/quotes/components/VendorPicker'
import { toOptions } from '@/components/ui/control'
import { FormSheet } from '@/components/ui/FormSheet'
import { IconButton } from '@/components/ui/IconButton'
import { Sheet } from '@/components/ui/Sheet'
import { useFormDraft } from '@/components/ui/useFormDraft'
import { Input } from '@/components/ui/Input'
import { CarrierSelect } from '@/features/cases/components/CarrierSelect'
import { useTransportRefs } from '@/features/directories/api/useDirectory'
import { useDirectionTemplates } from '@/features/settings/api/useDirectionTemplates'
import { isSuppressed } from '@/types/domain'
import type {
  CaseLeg,
  CaseParticipant,
  DirectionTemplate,
  ExpenseLine,
  LegStatus,
  Maybe,
  ParticipantRole,
} from '@/types/domain'

/* The case-detail editors: participants and expenses as sheets, and the leg's
 * FIELD GROUP for the two surfaces that are no longer sheets at all.
 *
 * The two sheets are what Operations touches daily, so they follow the same
 * shape as every other sheet in the product rather than inventing a
 * faster-looking one. Speed here comes from the sheet being the same sheet —
 * the fields are where they were last time — not from a bespoke inline grid per
 * section.
 *
 * A LEG is not among them any more. Amending one moved to `/cases/:id/legs/:id`
 * when a direction acquired a route chain (issue #35 story 3), and creating one
 * followed it to `/cases/:id/legs/new`: a 440px modal over a list could hold
 * neither the chain nor the trucks nor the money, and a create that opened in a
 * sheet while the record it made lived on a page was the last surface still
 * saying a direction is a dialog. What stayed here is `LegFields` — the one
 * definition of what a leg is made of, shared by both pages, because the PUT
 * behind them replaces the WHOLE record and a second copy of this markup would
 * quietly clear whatever it forgot to render.
 */

/** A suppressed value never loads into an editable input (AD-6). */
function editableValue(value: Maybe<string> | undefined): string {
  if (value === undefined || isSuppressed(value)) return ''
  return value
}

function isReadOnly(value: Maybe<string> | undefined): boolean {
  return isSuppressed(value)
}

/** Accepts `1234.56`, `1 234,56`; rejects text and blanks. Money is a STRING. */
function isValidAmount(raw: string): boolean {
  const normalised = raw.replace(/\s/g, '').replace(',', '.')
  return normalised !== '' && /^-?\d+(\.\d{1,2})?$/.test(normalised)
}

const LEG_STATUSES: LegStatus[] = [
  'planned',
  'in-transit',
  'waiting-border',
  'arrived',
  'completed',
]
const PARTICIPANT_ROLES: ParticipantRole[] = [
  'shipper',
  'consignee',
  'agent',
  'counter-agent',
  'vehicle-owner',
  'dispatcher',
  'driver',
]

/* ── The delete row, once ──────────────────────────────────────────────────── */

/** The dangerous action, below the fields and behind two presses.
 *
 *  ONE implementation for all three sheets in this file. The quote's own
 *  participant sheet has this block inlined, and inlining it a second, third and
 *  fourth time here would be four places for the confirm-reset bug to be fixed
 *  in — and that bug is subtle enough to be worth fixing once: a half-pressed
 *  delete must not survive the sheet closing, or reopening on another record
 *  puts the dangerous button under a click meant for something else.
 *
 *  Reset by KEY during render rather than in an effect, the pattern
 *  `useFormDraft` and `DatePicker` document: FormSheet keeps its markup mounted
 *  (Base UI `keepMounted`), so nothing unmounts to clear this, and an effect
 *  would paint one frame with the confirmation already showing.
 *
 *  Below the fields and separated by a rule rather than beside Save, so the
 *  dangerous action is off the same muscle memory as the safe one. */
function DeleteRow({
  onDelete,
  pending,
  resetKey,
  label,
  confirm,
  confirmAction,
}: {
  onDelete: () => void
  pending?: boolean
  /** Identifies the record the confirmation belongs to. */
  resetKey: string | null
  label: string
  confirm: string
  confirmAction: string
}) {
  const { t } = useTranslation()
  const [confirming, setConfirming] = useState(false)
  const [confirmedFor, setConfirmedFor] = useState<string | null>(resetKey)

  if (confirmedFor !== resetKey) {
    setConfirmedFor(resetKey)
    setConfirming(false)
  }

  return (
    <div className="border-line-hair mt-1 border-t pt-3">
      {confirming ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-text-body flex-1 text-[12.5px] leading-snug">
            {confirm}
          </span>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setConfirming(false)}
            disabled={pending}
          >
            {t('common.actions.cancel')}
          </Button>
          <Button variant="danger" size="sm" onClick={onDelete} disabled={pending}>
            {confirmAction}
          </Button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setConfirming(true)}
          disabled={pending}
          className="text-negative cursor-pointer text-[12.5px] disabled:cursor-not-allowed disabled:opacity-50"
        >
          {label}
        </button>
      )}
    </div>
  )
}

/* ── Leg ──────────────────────────────────────────────────────────────────── */

/** One truck as the leg form holds it — the WHOLE row, never the plate alone.
 *
 *  It was a bare `string` for exactly as long as a truck was a bare plate. Once
 *  story 5 gave the row a trailer, a container, a CMR number, a haulier, a
 *  driver, a telephone and a weight, a draft carrying only the plate stopped
 *  being a simplification and became a data loss: the leg PUT replaces the whole
 *  vehicle list and the server UPDATEs every column of a row it matches by
 *  plate, so a payload of `[{plate}]` blanked the other seven fields on every
 *  truck of the direction — under a save that reported success.
 *
 *  The repeater below still edits only the plate, which is the right amount of
 *  form for a sheet: `VehiclesTable` is where a truck's facts are entered. What
 *  the draft owes them is safe passage.
 *
 *  `weightKg` stays the wire's `number`, unlike every other field here, because
 *  no control on this surface types it — a string would be a parse this form has
 *  no reason to perform and one more way to lose the kilograms. */
export interface LegVehicleDraft {
  plate: string
  trailerPlate: string
  containerNumber: string
  cmrNumber: string
  /* Blanked when the server withheld them (AD-6) — see `legDraftFrom`. The
     surfaces that edit a leg REFUSE THE SAVE in that state rather than
     submitting the blanks; a form that may not read the haulier may not erase
     it either. */
  carrierId: string
  carrierName: string
  driverName: string
  driverPhone: string
  weightKg?: number
}

export interface LegDraft {
  origin: string
  destination: string
  /* ISO 3166-1 alpha-2 beside each place, never instead of it. The quote's
     route form made the opposite choice — its endpoint field IS the country —
     and a leg deliberately keeps both: "Khorgos" is where the cargo physically
     sits, and "KZ" is what a corridor report groups by. */
  originCountry: string
  destinationCountry: string
  /* The directory row's id, not a mode CODE. The server takes a
     `transportModeId` because a leg's mode is a reference into transport_refs,
     which the desk maintains (FR-43) — a code would be this form inventing a
     vocabulary the directory already owns. */
  transportModeId: string
  status: LegStatus
  /* The carrier as BOTH: the directory link the picker sets, and the name.
     The name is not redundant — a leg linked to nobody keeps what was typed off
     its waybill, and re-saving such a leg after an unrelated status change must
     not erase it. See `toLegPayload`. */
  carrierId: string
  carrierName: string
  reference: string
  /* The conveyance — a flight number on air, a voyage on sea (FR-26). Its own
     field because an air leg has BOTH this and a waybill, and one input for
     the pair would make the desk choose which fact to keep. */
  voyageNumber: string
  /* Held as plain strings while the sheet is open, and turned back into the
     server's shapes on submit. A draft carrying ids would have to invent them
     for rows the user just typed, and the server allocates them anyway — see
     `toLegPayload`. */
  vehicles: LegVehicleDraft[]
  containers: { number: string; sealNumber: string; type: string }[]
  etd: string
  eta: string
  /* The four facts that only exist once the leg has MOVED (issue #35 story 3).
     Optional rather than required, and that asymmetry is deliberate: the CREATE
     sheet does not ask them — an actual departure on a leg nobody has entered
     yet is not a fact anybody holds — while the leg page, which edits a leg
     that is running, asks all four. `toLegPayload` reads an absent one as
     "nobody stated this", which is what every other optional on that payload
     means. */
  atd?: string
  ata?: string
  stallNote?: string
  lastLocation?: string
}

/** Every field a leg carries, as controls.
 *
 *  Extracted from the sheet when the leg got a PAGE (issue #35 story 3): the
 *  page edits the same record with the same whole-record PUT, and two copies of
 *  this markup would be two answers to what a leg is made of — with the second
 *  copy quietly clearing whatever it forgot to render, because the PUT replaces
 *  everything it is not sent.
 *
 *  The container decides the frame; this decides the fields. */
export function LegFields({
  draft,
  setDraft,
  touched,
  record,
  actuals,
  lockedEnds,
}: {
  draft: LegDraft
  setDraft: (next: LegDraft) => void
  /** Whether the surface has been submitted once — errors stay quiet until it
   *  has, so a blank form does not open shouting. */
  touched: boolean
  /** The leg being edited, read ONLY for the suppression the carrier control
   *  honours (AD-6). Absent when creating. */
  record?: CaseLeg
  /** Also ask the facts that only exist once the leg is running: the actual
   *  dates, where it was last seen, and why it is standing still. Off on the
   *  create sheet, where none of them can be true yet. */
  actuals?: boolean
  /** Why the two ends are SHOWN rather than asked — a sentence, or absent.
   *
   *  The create surface passes one once a direction template is chosen, because
   *  the ends are then not this form's to decide: applying a corridor calls
   *  `SetLegEnds` in the same transaction and overwrites both of them with the
   *  chain's first and last point. Leaving the controls live meant the reader
   *  typed four fields that were silently replaced a second later, which is not
   *  a validation problem — it is a form asking a question whose answer it
   *  throws away. */
  lockedEnds?: string
}) {
  const { t } = useTranslation()
  const { data: modes } = useTransportRefs('mode', true)

  /* No complaint about an end the reader was not allowed to type. The values are
     the template's and the server will re-derive them anyway. */
  const originError =
    touched && !lockedEnds && !draft.origin.trim() ? t('common.form.required') : undefined
  const destinationError =
    touched && !lockedEnds && !draft.destination.trim()
      ? t('common.form.required')
      : undefined
  /* Required here and required on the server: unlike a case header, which the
     desk opens before the routing is decided, a LEG is a movement — and a
     movement with no mode is not one. */
  const modeError =
    touched && !draft.transportModeId ? t('common.form.required') : undefined

  return (
    <>
      {/* The route, as a PLACE and a COUNTRY per end.
       *
       * Two fields rather than the quote route form's one, and the difference
       * is deliberate rather than an inconsistency: `RouteOptionsCard` stores
       * the country IN the endpoint, so a saved option reads "CN → KZ → TM". A
       * leg endpoint is a terminal, a siding or a yard whose name Operations
       * types off a carrier's email, and "Khorgos" is the fact the desk needs
       * on the phone — the country is a second, structured fact for the reports
       * that group by corridor.
       *
       * The country control is the flag ALONE once chosen, and it LEADS the
       * row. A country narrows the place, so it is answered first and read
       * first — the same order a customs line takes ("KZ, Khorgos") and the
       * order the leg card already renders. A second full-width control
       * repeating "Kazakhstan" would compete with "Khorgos" for the eye on a
       * row that is really one fact, so it stays the flag alone. Its name is
       * still spoken (CountryDropdown composes the accessible name) and still
       * labels every row in the list. */}
      <div className="flex gap-2.5">
        <Field
          label={t('quotes.form.origin')}
          required
          error={originError}
          hint={lockedEnds}
          className="flex-1"
        >
          {(props) => (
            <div className="flex items-start gap-1.5">
              <CountryDropdown
                flagOnly
                disabled={lockedEnds !== undefined}
                value={draft.originCountry}
                onChange={(originCountry) => setDraft({ ...draft, originCountry })}
                aria-label={t('cases.legs.originCountry')}
                className="w-auto shrink-0 px-2.5"
              />
              <Input
                disabled={lockedEnds !== undefined}
                value={draft.origin}
                onChange={(e) => setDraft({ ...draft, origin: e.target.value })}
                className="min-w-0 flex-1"
                {...props}
              />
            </div>
          )}
        </Field>

        <Field
          label={t('quotes.form.destination')}
          required
          error={destinationError}
          hint={lockedEnds}
          className="flex-1"
        >
          {(props) => (
            <div className="flex items-start gap-1.5">
              <CountryDropdown
                flagOnly
                disabled={lockedEnds !== undefined}
                value={draft.destinationCountry}
                onChange={(destinationCountry) =>
                  setDraft({ ...draft, destinationCountry })
                }
                aria-label={t('cases.legs.destinationCountry')}
                className="w-auto shrink-0 px-2.5"
              />
              <Input
                disabled={lockedEnds !== undefined}
                value={draft.destination}
                onChange={(e) => setDraft({ ...draft, destination: e.target.value })}
                className="min-w-0 flex-1"
                {...props}
              />
            </div>
          )}
        </Field>
      </div>

      <Field label={t('quotes.form.mode')} required error={modeError}>
        {(props) => (
          <Select
            value={draft.transportModeId}
            onChange={(transportModeId) => setDraft({ ...draft, transportModeId })}
            options={(modes ?? []).map((mode) => ({
              value: mode.id,
              // The directory's own label, not a translated code: transport
              // refs are maintained by the desk (FR-43), so entries exist that
              // no catalogue key covers.
              label: mode.label,
              hint: mode.code,
            }))}
            placeholder={t('common.form.selectPlaceholder')}
            {...props}
          />
        )}
      </Field>

      <Field label={t('common.fields.status')} required>
        {(props) => (
          <Select
            value={draft.status}
            onChange={(status) => setDraft({ ...draft, status })}
            options={toOptions(LEG_STATUSES, (status) => t(`legStatus.${status}`))}
            {...props}
          />
        )}
      </Field>

      {/* The carrier, picked from the directory (FR-40) rather than typed.
       *
       * Still suppressed for Sales exactly as the free-text field was, and now
       * on BOTH halves: the server withholds the id under the same call as the
       * name, so a caller who may not read who carried a leg cannot read the
       * key that resolves it either. The control is disabled rather than
       * hidden, so the field still says the fact exists and is withheld
       * (AD-6) — and `editableValue` above already refused to load a
       * suppression marker into the draft, so a re-enabled control still could
       * not write one back. */}
      <Field
        label={t('cases.legs.carrier')}
        hint={isReadOnly(record?.carrierName) ? t('common.suppressed.reason') : undefined}
      >
        {(props) => (
          <CarrierSelect
            value={draft.carrierId}
            name={draft.carrierName}
            disabled={isReadOnly(record?.carrierName)}
            onChange={(carrier) =>
              setDraft({ ...draft, carrierId: carrier.id, carrierName: carrier.name })
            }
            {...props}
          />
        )}
      </Field>

      <Field label={t('cases.legs.reference')} hint={t('common.form.optional')}>
        {(props) => (
          <Input
            value={draft.reference}
            onChange={(e) => setDraft({ ...draft, reference: e.target.value })}
            className="font-mono"
            {...props}
          />
        )}
      </Field>

      {/* The conveyance (FR-26). Beneath the waybill rather than beside it,
          because they are read together and an air leg fills in both — putting
          them on one row would suggest choosing between them.

          The LABEL follows the mode: a rail leg has no voyage number and a sea
          leg has no flight, and one label reading "flight / voyage number"
          would make the desk pause on every leg to work out which half applies
          to them. */}
      <Field
        label={t(
          draft.transportModeId &&
            modes?.find((mode) => mode.id === draft.transportModeId)?.code === 'AIR'
            ? 'cases.legs.flightNumber'
            : 'cases.legs.voyageNumber',
        )}
        hint={t('common.form.optional')}
      >
        {(props) => (
          <Input
            value={draft.voyageNumber}
            onChange={(e) => setDraft({ ...draft, voyageNumber: e.target.value })}
            className="font-mono"
            {...props}
          />
        )}
      </Field>

      {/* Truck plates (FR-67). Plural on ONE leg — a consignment crossing on
          two trucks is one leg and two vehicles, and this is the first version
          of this sheet that can say so.

          One INPUT per truck and nine fields per row: everything the row is not
          asking about is carried through the edit untouched (`...vehicle`),
          because the PUT this feeds replaces the whole list. A repeater that
          spread only the plate back would leave the trailer, the CMR number and
          the haulier of every truck here to be cleared by the save. The rest of
          the row is entered on «Фуры и ставки», which is the surface that has
          the width for twelve columns. */}
      <Field label={t('cases.legs.vehicles')} hint={t('common.form.optional')}>
        {() => (
          <div className="flex flex-col gap-2">
            {draft.vehicles.map((vehicle, index) => (
              <div key={index} className="flex items-center gap-2">
                <Input
                  value={vehicle.plate}
                  aria-label={t('cases.legs.plateN', { n: index + 1 })}
                  placeholder={t('cases.legs.platePlaceholder')}
                  className="flex-1 font-mono"
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      vehicles: draft.vehicles.map((row, i) =>
                        i === index ? { ...row, plate: e.target.value } : row,
                      ),
                    })
                  }
                />
                <IconButton
                  icon={Trash2}
                  size="sm"
                  label={t('cases.legs.removePlate')}
                  onClick={() =>
                    setDraft({
                      ...draft,
                      vehicles: draft.vehicles.filter((_, i) => i !== index),
                    })
                  }
                />
              </div>
            ))}
            <Button
              variant="ghost"
              size="sm"
              iconLeft={Plus}
              className="self-start"
              onClick={() =>
                setDraft({ ...draft, vehicles: [...draft.vehicles, emptyVehicle()] })
              }
            >
              {t('cases.legs.addPlate')}
            </Button>
          </div>
        )}
      </Field>

      {/* Containers and their seals (FR-67), as PAIRS. One row per box, never
          two parallel lists: a seal typed against the wrong container is worse
          than no seal at all, and parallel lists are how they drift apart. */}
      <Field label={t('cases.legs.containers')} hint={t('common.form.optional')}>
        {() => (
          <div className="flex flex-col gap-2">
            {draft.containers.map((box, index) => (
              <div key={index} className="flex items-center gap-2">
                <Input
                  value={box.number}
                  aria-label={t('cases.legs.containerN', { n: index + 1 })}
                  placeholder={t('cases.legs.containerPlaceholder')}
                  className="flex-1 font-mono"
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      containers: draft.containers.map((b, i) =>
                        i === index ? { ...b, number: e.target.value } : b,
                      ),
                    })
                  }
                />
                <Input
                  value={box.sealNumber}
                  aria-label={t('cases.legs.sealN', { n: index + 1 })}
                  placeholder={t('cases.legs.sealPlaceholder')}
                  className="flex-1 font-mono"
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      containers: draft.containers.map((b, i) =>
                        i === index ? { ...b, sealNumber: e.target.value } : b,
                      ),
                    })
                  }
                />
                <Input
                  value={box.type}
                  aria-label={t('cases.legs.containerTypeN', { n: index + 1 })}
                  placeholder={t('cases.legs.containerTypePlaceholder')}
                  className="w-20"
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      containers: draft.containers.map((b, i) =>
                        i === index ? { ...b, type: e.target.value } : b,
                      ),
                    })
                  }
                />
                <IconButton
                  icon={Trash2}
                  size="sm"
                  label={t('cases.legs.removeContainer')}
                  onClick={() =>
                    setDraft({
                      ...draft,
                      containers: draft.containers.filter((_, i) => i !== index),
                    })
                  }
                />
              </div>
            ))}
            <Button
              variant="ghost"
              size="sm"
              iconLeft={Plus}
              className="self-start"
              onClick={() =>
                setDraft({
                  ...draft,
                  containers: [
                    ...draft.containers,
                    { number: '', sealNumber: '', type: '' },
                  ],
                })
              }
            >
              {t('cases.legs.addContainer')}
            </Button>
          </div>
        )}
      </Field>

      <div className="flex gap-2.5">
        <Field label={t('cases.form.etd')} className="flex-1">
          {(props) => (
            <DatePicker
              value={draft.etd}
              onChange={(etd) => setDraft({ ...draft, etd })}
              {...props}
            />
          )}
        </Field>

        {/* A leg cannot arrive before it departs. */}
        <Field label={t('cases.columns.eta')} className="flex-1">
          {(props) => (
            <DatePicker
              value={draft.eta}
              min={draft.etd || undefined}
              onChange={(eta) => setDraft({ ...draft, eta })}
              {...props}
            />
          )}
        </Field>
      </div>

      {/* What actually happened, asked only where it can have happened.
       *
       * These four travelled to the browser on every leg read and no form asked
       * for any of them: the ATD and ATA were writable only by whatever wrote
       * the fixture, and the stall note the case screen PRINTS in an amber
       * banner could not be typed anywhere in the application. A whole-record
       * PUT that never sends a field is also a PUT that clears it, so the
       * omission was not merely a gap — it was a form that quietly dropped the
       * dispatcher's note the next time somebody corrected a date. */}
      {actuals ? (
        <>
          <div className="flex gap-2.5">
            <Field label={t('cases.legPage.atd')} className="flex-1">
              {(props) => (
                <DatePicker
                  value={draft.atd ?? ''}
                  onChange={(atd) => setDraft({ ...draft, atd })}
                  {...props}
                />
              )}
            </Field>

            <Field label={t('cases.legPage.ata')} className="flex-1">
              {(props) => (
                <DatePicker
                  value={draft.ata ?? ''}
                  /* A leg cannot arrive before it left, in fact any more than
                     in plan — and the actual departure is the floor here, not
                     the planned one. */
                  min={draft.atd || undefined}
                  onChange={(ata) => setDraft({ ...draft, ata })}
                  {...props}
                />
              )}
            </Field>
          </div>

          <Field
            label={t('cases.legs.lastLocation')}
            hint={t('cases.legPage.lastLocationHint')}
          >
            {(props) => (
              <Input
                value={draft.lastLocation ?? ''}
                onChange={(e) => setDraft({ ...draft, lastLocation: e.target.value })}
                {...props}
              />
            )}
          </Field>

          <Field label={t('cases.legPage.stallNote')} hint={t('common.form.optional')}>
            {(props) => (
              <Input
                value={draft.stallNote ?? ''}
                onChange={(e) => setDraft({ ...draft, stallNote: e.target.value })}
                {...props}
              />
            )}
          </Field>
        </>
      ) : null}
    </>
  )
}

/** The direction a NEW leg's chain is filled from (issue #35 story 3).
 *
 *  Offered on creation only, which is why it is not part of `LegFields`:
 *  applying a direction OVERWRITES the chain, and a picker sitting on an amend
 *  form would put that overwrite one stray click away from a leg whose arrivals
 *  somebody has been recording for a fortnight. On the leg's own page it is a
 *  deliberate action behind a warning instead.
 *
 *  Optional, and the hint says what choosing one does: the points are COPIED,
 *  so the corridor can be edited in Settings afterwards without moving this
 *  shipment.
 *
 *  Its own component rather than markup on the create page, for `LegFields`'
 *  reason: this control and the leg it belongs to are one form, and the day a
 *  second surface offers a template on a create it must offer the same list
 *  with the same warning in it. */
export function DirectionTemplateField({
  value,
  onChange,
}: {
  value: string
  /** The chosen corridor, and the RECORD beside its id.
   *
   *  The record travels because the caller needs the two ends off it — the
   *  chain's first and last point become the leg's own, so a create that made
   *  the reader type them was asking for four values the apply overwrites. It is
   *  handed over rather than re-fetched: this component already holds the list,
   *  and a second `useDirectionTemplates` at the call site would be a second
   *  cache entry that can answer differently. */
  onChange: (templateId: string, template?: DirectionTemplate) => void
}) {
  const { t } = useTranslation()
  /* Only the LIVE directions: Settings keeps retired corridors maintainable,
     and a picker that offered them would fill a new leg from a route the desk
     has decided is no longer run. */
  const { data: directions } = useDirectionTemplates({ activeOnly: true })

  return (
    <Field
      label={t('cases.legPage.directionTemplate')}
      hint={t('cases.legPage.directionTemplateHint')}
    >
      {(props) => (
        <Select
          value={value}
          onChange={(id) =>
            onChange(id, (directions ?? []).find((direction) => direction.id === id))
          }
          options={(directions ?? []).map((direction) => ({
            value: direction.id,
            label: direction.name,
            hint: t('settings.directions.pointCount', {
              count: direction.points.length,
            }),
          }))}
          placeholder={t('cases.legPage.directionTemplateNone')}
          {...props}
        />
      )}
    </Field>
  )
}

/** A truck row the repeater has just opened: a plate to be typed and eight
 *  fields nobody has answered. A FUNCTION rather than a frozen constant, so two
 *  rows added in a row are two objects — the spread edit above replaces the row
 *  it touches, and a shared literal would make that replacement invisible to a
 *  structural dirty check. */
function emptyVehicle(): LegVehicleDraft {
  return {
    plate: '',
    trailerPlate: '',
    containerNumber: '',
    cmrNumber: '',
    carrierId: '',
    carrierName: '',
    driverName: '',
    driverPhone: '',
  }
}

/* `emptyLegDraft` — the blank a create form seeds from — lives beside
   `legDraftFrom` and `toLegPayload` in `api/useCase.ts`. The three have to agree
   about what a leg is made of, and a component module may export only
   components. */

/* ── Participant ──────────────────────────────────────────────────────────── */

export interface CaseParticipantDraft {
  role: ParticipantRole
  name: string
  contact: string
  notifyByEmail: boolean
}

export function CaseParticipantFormSheet({
  open,
  onClose,
  record,
  onSubmit,
  onDelete,
  pending,
  error,
}: {
  open: boolean
  onClose: () => void
  record?: CaseParticipant
  onSubmit: (values: CaseParticipantDraft) => void
  /** Takes a party off the case. Absent when adding. */
  onDelete?: () => void
  pending?: boolean
  /** A refusal from the server, shown above the fields with the draft intact. */
  error?: string
}) {
  const { t } = useTranslation()
  const [draft, setDraft, touched, markTouched] = useFormDraft<CaseParticipantDraft>(
    open ? (record?.id ?? 'new') : null,
    () =>
      record
        ? {
            role: record.role,
            name: editableValue(record.name),
            contact: editableValue(record.contact),
            notifyByEmail: record.notifyByEmail,
          }
        : emptyCaseParticipant,
  )

  // A suppressed name cannot be validated as present — the viewer simply cannot
  // see it — so the requirement only applies where the field is editable.
  const nameSuppressed = isReadOnly(record?.name)
  const nameError =
    touched && !nameSuppressed && !draft.name.trim()
      ? t('common.form.required')
      : undefined
  const missingName = !nameSuppressed && !draft.name.trim()

  /* The rule that couples two fields: a party asked to be emailed needs an
     address. The server holds it too (`casefile/participants.go` refuses
     notifyByEmail without a `%_@_%` contact), and until the tickbox started
     ticked that refusal was rare enough to live only there. It is not rare any
     more — the box arrives checked (FR-48, AC 6) — and THIS section shows no
     mutation error, so a server refusal would land nowhere at all and the save
     would look like a button that does nothing. Checked here, the correction
     happens while both fields are still on screen. (The section shows mutation
     errors now — see `error` — but the local check stays: a correction offered
     beside the field beats one offered above the record.)

     Reported on the CONTACT rather than on the tickbox, following the quote
     sheet: the user ticked the box deliberately, and telling them the tickbox
     is wrong invites them to untick it, which is the opposite of what they
     wanted. */
  const contactSuppressed = isReadOnly(record?.contact)
  const needsEmail =
    !contactSuppressed && draft.notifyByEmail && !looksLikeEmail(draft.contact)
  const contactError =
    touched && needsEmail ? t('cases.participants.contactNeededForEmail') : undefined

  const invalid = missingName || needsEmail

  return (
    <FormSheet
      open={open}
      onClose={onClose}
      title={record ? t('cases.participants.edit') : t('cases.participants.add')}
      pending={pending}
      error={error ?? (touched && invalid ? t('common.form.fixFields') : undefined)}
      onSubmit={() => {
        markTouched()
        if (invalid) return
        onSubmit(draft)
      }}
    >
      <Field label={t('cases.participants.role')} required>
        {(props) => (
          <Select
            value={draft.role}
            onChange={(role) => setDraft({ ...draft, role })}
            options={toOptions(PARTICIPANT_ROLES, (role) =>
              t(`participantRole.${role}`),
            )}
            {...props}
          />
        )}
      </Field>

      <Field
        label={t('common.fields.name')}
        required={!nameSuppressed}
        error={nameError}
        hint={nameSuppressed ? t('common.suppressed.reason') : undefined}
      >
        {(props) => (
          <Input
            value={draft.name}
            disabled={nameSuppressed}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            {...props}
          />
        )}
      </Field>

      <Field
        label={t('common.fields.contact')}
        error={contactError}
        hint={
          contactSuppressed
            ? t('common.suppressed.reason')
            : t('cases.participants.contactHint')
        }
      >
        {(props) => (
          <Input
            value={draft.contact}
            disabled={contactSuppressed}
            onChange={(e) => setDraft({ ...draft, contact: e.target.value })}
            {...props}
          />
        )}
      </Field>

      <Checkbox
        label={t('cases.participants.notifyByEmail')}
        checked={draft.notifyByEmail}
        onCheckedChange={(checked) => setDraft({ ...draft, notifyByEmail: checked })}
      />

      {onDelete ? (
        <DeleteRow
          onDelete={onDelete}
          pending={pending}
          resetKey={open ? (record?.id ?? 'new') : null}
          label={t('cases.participants.delete')}
          confirm={t('cases.participants.deleteConfirm')}
          confirmAction={t('cases.participants.deleteConfirmAction')}
        />
      ) : null}
    </FormSheet>
  )
}

const emptyCaseParticipant: CaseParticipantDraft = {
  role: 'shipper',
  name: '',
  contact: '',
  /* Ticked on a NEW party (FR-48, Story 8.5 AC 6). An external participant has
     no login and no bell — email is the ONLY way they hear that they are on
     this shipment — so an unticked default means the common case is served by
     somebody remembering to tick a box, and the party who was forgotten never
     learns they were forgotten. The one email this sends is assignment-time
     only, so the cost of a wrong default in this direction is one message.

     It seeds the ADD form only: an existing party's stored preference is read
     from the record above, so somebody who unticked it stays unticked. */
  notifyByEmail: true,
}

/** An address the server's `%_@_%` check would accept.
 *
 *  Deliberately as weak as that column constraint rather than stricter: this
 *  field also holds phone numbers, and the question is only whether a party who
 *  ASKED to be emailed carries something shaped like an address. Restated here
 *  rather than imported from `features/quotes`: the two sections are different
 *  features answering to different servers' validation, and a shared helper
 *  would tie a case sheet to the quote draft module for four lines. */
function looksLikeEmail(contact: string): boolean {
  /* `%_@_%` says: SOME '@' has at least one character on each side of it —
     which is not the same as "the first '@' does", and the difference is a real
     value: `@x@y` matches the column constraint and would have been refused
     here. Slicing off the first and last characters and asking whether an '@'
     survives is that predicate exactly, and it stays exact for `a@b@` too,
     where a first-or-last-index test gets the wrong answer in the other
     direction. Client-stricter would be safe; client-DIFFERENT is what makes a
     comment like the one above stop being true. */
  const trimmed = contact.trim()
  return trimmed.slice(1, -1).includes('@')
}

/* ── Expense ──────────────────────────────────────────────────────────────── */

export interface ExpenseDraft {
  description: string
  counterpartyName: string
  /** The directory row the name resolves to, or null when it resolves to none.
   *  DERIVED by the picker from the text, never typed — see VendorPicker. */
  vendorId: string | null
  planned: string
  actual: string
  currency: string
  settled: boolean
}

const CURRENCIES = ['USD', 'EUR', 'CNY', 'RUB', 'TMT'] as const

export function ExpenseFormSheet({
  open,
  onClose,
  record,
  onSubmit,
  onDelete,
  pending,
  error,
}: {
  open: boolean
  onClose: () => void
  record?: ExpenseLine
  onSubmit: (values: ExpenseDraft) => void
  /** Removes the whole cost line — the plan and every actual against it, in one
   *  transaction. Absent when adding.
   *
   *  A refusal reaches the reader through `error`, with the draft still on
   *  screen. */
  onDelete?: () => void
  pending?: boolean
  /** A refusal from the server, shown above the fields with the draft intact. */
  error?: string
}) {
  const { t } = useTranslation()
  const [draft, setDraft, touched, markTouched] = useFormDraft<ExpenseDraft>(
    open ? (record?.id ?? 'new') : null,
    () =>
      record
        ? {
            description: record.description,
            counterpartyName: editableValue(record.counterpartyName),
            /* Seeded from the SERVER's link rather than re-derived from the
               name. Re-deriving looks equivalent and is not: a counterparty
               renamed in the directory since this line was booked no longer
               matches by text, so an edit that only touched the amount would
               post a null id and quietly unlink it.

               `|| null` because editableValue answers '' for both absent and
               suppressed, and the payload's shape is "an id or none". */
            vendorId: editableValue(record.vendorId) || null,
            planned: record.planned.amount,
            actual: record.actual?.amount ?? '',
            currency: record.planned.currency,
            settled: record.settled,
          }
        : emptyExpense,
  )

  /* THE LINE THIS SHEET CANNOT EDIT. A plan settled by two vendors' invoices is
     legal and AD-4 keeps it — `actualLines` is the DTO field that says so — but
     it has no single actual figure, so there is nothing for the field below to
     hold and no draft the server could accept. It refuses such a save with a
     409, and a form that collects typing the server will certainly reject is
     worse than no form: it spends the reader's attention and then loses it.

     So this state SAYS so and offers no save, rather than disabling one — a
     greyed button with no explanation is the same dead end without the sentence.
     Nothing extra is fetched to know this: the count rides the folded line. */
  const actualLines = record?.actualLines ?? 0
  if (record && actualLines > 1) {
    return (
      <Sheet
        open={open}
        onClose={onClose}
        title={t('cases.money.editExpense')}
        footer={
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button variant="secondary" size="lg" onClick={onClose} className="sm:w-auto">
              {t('common.actions.close')}
            </Button>
          </div>
        }
      >
        <div className="flex flex-col gap-2 pt-1">
          <p className="text-text-heading text-[13px] font-medium">
            {t('cases.money.severalActualsTitle')}
          </p>
          <p className="text-text-muted text-[12.5px] leading-snug font-light">
            {t('cases.money.severalActualsDescription', {
              description: record.description,
              lines: actualLines,
            })}
          </p>
        </div>
      </Sheet>
    )
  }

  const descriptionError =
    touched && !draft.description.trim() ? t('common.form.required') : undefined
  const plannedError =
    touched && !isValidAmount(draft.planned) ? t('common.form.invalidNumber') : undefined
  // Actual is genuinely optional — it does not exist until the cost lands — so
  // an empty value is valid and only a non-empty malformed one is an error.
  const actualError =
    touched && draft.actual.trim() !== '' && !isValidAmount(draft.actual)
      ? t('common.form.invalidNumber')
      : undefined
  const invalid =
    !draft.description.trim() ||
    !isValidAmount(draft.planned) ||
    (draft.actual.trim() !== '' && !isValidAmount(draft.actual))

  return (
    <FormSheet
      open={open}
      onClose={onClose}
      title={record ? t('cases.money.editExpense') : t('cases.money.addExpense')}
      pending={pending}
      error={error ?? (touched && invalid ? t('common.form.fixFields') : undefined)}
      onSubmit={() => {
        markTouched()
        if (invalid) return
        onSubmit(draft)
      }}
    >
      <Field
        label={t('cases.money.columns.description')}
        required
        error={descriptionError}
      >
        {(props) => (
          <Input
            value={draft.description}
            onChange={(e) => setDraft({ ...draft, description: e.target.value })}
            {...props}
          />
        )}
      </Field>

      <Field
        label={t('cases.money.columns.counterparty')}
        hint={
          isReadOnly(record?.counterpartyName)
            ? t('common.suppressed.reason')
            : undefined
        }
      >
        {(props) => (
          /* The directory, not a text box (FR-30). Free text stays possible and
             is the requirement rather than a fallback: FR-20 expects a vendor
             invoice to arrive before anybody has made a participant row, and
             FR-29 hides agent rows from Sales entirely — a closed list would
             have taken from them the ability to record an agent as a vendor.

             The same control the quote's costing table uses, imported rather
             than copied: a second one would be a second opinion about how a
             name becomes a link, and both surfaces feed one payables report. */
          <VendorPicker
            value={draft.counterpartyName}
            disabled={isReadOnly(record?.counterpartyName)}
            onChange={(name, vendorId) =>
              setDraft({ ...draft, counterpartyName: name, vendorId })
            }
            {...props}
          />
        )}
      </Field>

      <div className="flex gap-2.5">
        <Field
          label={t('cases.money.columns.planned')}
          required
          error={plannedError}
          className="flex-1"
        >
          {(props) => (
            <Input
              inputMode="decimal"
              value={draft.planned}
              onChange={(e) => setDraft({ ...draft, planned: e.target.value })}
              className="asa-num"
              {...props}
            />
          )}
        </Field>

        <Field label={t('finance.payments.currency')} className="w-28 shrink-0">
          {(props) => (
            <Select
              value={draft.currency}
              onChange={(currency) => setDraft({ ...draft, currency })}
              // A currency code is already its own label.
              options={toOptions(CURRENCIES, (currency) => currency)}
              {...props}
            />
          )}
        </Field>
      </div>

      {/* Variance is DERIVED from planned and actual and is never entered: any
          figure computed from editable inputs recalculates itself, and no
          "recalculate" button exists anywhere in the product. */}
      <Field
        label={t('cases.money.columns.actual')}
        error={actualError}
        hint={t('cases.money.actualHint')}
      >
        {(props) => (
          <Input
            inputMode="decimal"
            value={draft.actual}
            onChange={(e) => setDraft({ ...draft, actual: e.target.value })}
            className="asa-num"
            {...props}
          />
        )}
      </Field>

      <Checkbox
        label={t('cases.money.settled')}
        checked={draft.settled}
        onCheckedChange={(checked) => setDraft({ ...draft, settled: checked })}
      />

      {onDelete ? (
        <DeleteRow
          onDelete={onDelete}
          pending={pending}
          resetKey={open ? (record?.id ?? 'new') : null}
          label={t('cases.money.deleteExpense')}
          confirm={t('cases.money.deleteExpenseConfirm')}
          confirmAction={t('cases.money.deleteExpenseConfirmAction')}
        />
      ) : null}
    </FormSheet>
  )
}

const emptyExpense: ExpenseDraft = {
  description: '',
  counterpartyName: '',
  vendorId: null,
  planned: '',
  actual: '',
  currency: 'USD',
  settled: false,
}
